import { useLocalStorageState } from 'ahooks';
import { Form, FormInstance } from 'antd';
import { isEmpty, isPlainObject } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppConfig } from '@/config/context';
import { getProductId, isAndroid } from '@/infrastructure/product-info';
import { getCompareDateValue, searchValueDeserializer } from '@/infrastructure/utils/issue';
import { toArray } from '@/infrastructure/utils/to-array';
import {
  FormEntry,
  FormType, IFormRef,
  QueryForm,
} from '@/presentation/components/form';
import { DEFAULT_MONITOR_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import { VersionSelectGroupName } from '@/presentation/components/form/components/version-select-group';
import { FieldsOperator, LAUNCH_TIME_MAX_AND_FREE_MEM_OPERATOR } from '@/presentation/components/form/proto';
import { NUMBER_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import {
  getVersionLabel,
  onChangeAppVersion,
  getShowSwitchStatus,
} from '@/presentation/components/form/utils';
import { ABTEST_TIPS, BUNDLE_TIPS_CASE, hintText } from '@/presentation/components/hint-text/hintText';
import { enumFilterTips, numberFilterTips } from '@/presentation/components/issue-list/components/tips';
import { SEARCH_VALUES } from '@/presentation/config/storage';
import { SearchValueStorageType } from '@/presentation/config/type';
import {
  AppVersionOperator,
  FIELD_ALL,
  FIELD_LABEL,
  FieldKey,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { FieldList } from '@/proto/token';

interface IProps {
  onFinish: (value: Record<string, any>) => void;
  entry: FilterEntry;
  initialValue?: FieldList & { timeRange: string[], isValueInit?: boolean };
  onValueInited?: (value: Record<string, any>) => void;
  form: FormInstance;
  tokenInitValues: FieldList & { isValueInit?: boolean };
}

export const ProblemForm: FC<IProps> = observer((props) => {
  const { tokenInitValues } = props;
  const [showSwitch, setShowSwitch] = useState(false);
  const [topForm] = Form.useForm();
  const tableRef = useRef<IFormRef>(null);
  const form = props.form ? props.form : topForm;
  const [searchValues, setSearchValues] = useLocalStorageState<SearchValueStorageType>(`${SEARCH_VALUES}-${props.entry}-${getProductId()}`, {
    defaultValue: [],
  });
  const config = useAppConfig();

  useEffect(() => {
    if (!isEmpty(tokenInitValues)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = tokenInitValues;
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(entries, tokenInitValues)?.then(() => {
        form.setFieldsValue(tokenInitValues);
        if (isValueInit) {
          props.onValueInited?.(form.getFieldsValue());
        }
        // 初始化显示 【只显示新增问题】字段
        setShowSwitch(getShowSwitchStatus({
          values: toArray(appVersionGroup ?? ''),
          operator: appVersionOperator ?? '',
        }));
      });
    }
  }, [tokenInitValues]);

  useEffect(() => {
    if (!isEmpty(props.initialValue)) {
      const { isValueInit = true, appVersionGroup, appVersionOperator } = props.initialValue;
      form.setFieldsValue(props.initialValue);
      if (isValueInit) {
        props.onValueInited?.(form.getFieldsValue());
      }
      // 初始化显示 【只显示新增问题】字段
      setShowSwitch(getShowSwitchStatus({
        values: toArray(appVersionGroup ?? ''),
        operator: appVersionOperator ?? '',
      }));
    }
  }, [props.initialValue]);

  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      label: FIELD_LABEL[FieldKey.TimeRange],
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      limitDays: DEFAULT_MONITOR_LIMIT_DAYS,
      showTime: props.entry === FilterEntry.MemoryFdDetail || props.entry === FilterEntry.MemoryFdList,
      initTimeWindow: TimeType.MINUTE5,
      hiddenTimeWindow: props.entry !== FilterEntry.MemoryFdList,
      isIssuesTimeWindow: props.entry === FilterEntry.MemoryFdList,
    },
    {
      type: FormType.VersionSelectGroup,
      leftName: FieldKey.AppVersionOperator,
      rightName: FieldKey.AppVersion,
      label: FIELD_LABEL[FieldKey.AppVersionOperator],
      leftFieldKey: FieldKey.AppVersionOperator,
      rightFieldKey: FieldKey.AppVersion,
      fieldScope: Zone.Fd,
      scope: 'issues',
      mode: 'tags',
      leftInitialValue: AppVersionOperator.Or,
      rightInitialValue: [FIELD_ALL],
      name: VersionSelectGroupName.appVersionGroup,
      deleteable: false,
      hasMaxWidth: true,
    },
    {
      type: FormType.Select,
      name: 'appVersionMode',
      label: hintText(FIELD_LABEL[FieldKey.AppVersionMode], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.AppVersionMode],
      fieldKey: FieldKey.AppVersionMode,
      fieldScope: Zone.Fd,
      mode: 'multiple',
      initialValue: [FIELD_ALL],
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.Select,
      name: 'scene',
      label: hintText(FIELD_LABEL[FieldKey.Scene], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.Scene],
      fieldKey: FieldKey.Scene,
      fieldScope: Zone.Fd,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 500,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'brand',
      label: FIELD_LABEL[FieldKey.Brand],
      disabled: !isAndroid(),
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: 'processName',
      label: hintText(FIELD_LABEL[FieldKey.ProcessName], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.ProcessName],
      fieldKey: FieldKey.ProcessName,
      fieldScope: Zone.Fd,
      mode: 'single',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.VersionSelect,
      name: FieldKey.OsVersion,
      versionName: FieldKey.OsVersion,
      label: getVersionLabel(FIELD_LABEL[FieldKey.OsVersion]),
      fieldKey: FieldKey.OsVersion,
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      fieldScope: Zone.Fd,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: 'sdkVersion',
      label: hintText(FIELD_LABEL[FieldKey.SdkVersion], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: Zone.Fd,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 300,
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.AutoComplete,
      name: 'model',
      label: FIELD_LABEL[FieldKey.Model],
      fieldKey: FieldKey.Model,
      fieldScope: Zone.Fd,
      initialValue: FIELD_ALL,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Input,
      name: 'accountId',
      label: '用户ID',
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'qimei',
      label: '设备ID',
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'launchId',
      label: FIELD_LABEL[FieldKey.LaunchId],
      labelString: '启动ID',
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'processLaunchId',
      label: FIELD_LABEL[FieldKey.ProcessLaunchId],
      labelString: '进程启动ID',
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'clientIdentify',
      label: '消息ID',
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Input,
      name: 'feature',
      label: 'Issue ID',
      disabled: props.entry !== FilterEntry.MemoryFdList,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'funcName',
      label: '问题特征',
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Select,
      name: 'fdType',
      label: hintText(FIELD_LABEL[FieldKey.FdType], enumFilterTips),
      labelString: FIELD_LABEL[FieldKey.FdType],
      fieldKey: FieldKey.FdType,
      fieldScope: Zone.Fd,
      mode: 'single',
      initialValue: FIELD_ALL,
      disabled: props.entry !== FilterEntry.MemoryFdList,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Select,
      name: 'bundleId',
      label: hintText(FIELD_LABEL[FieldKey.BundleId], BUNDLE_TIPS_CASE),
      labelString: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      fieldScope: Zone.Fd,
      mode: 'single',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.SuspiciousCount,
      label: hintText('疑似泄漏FD数量', numberFilterTips),
      labelString: '疑似泄漏FD数量',
      group: FormColumnGroup.exceptionInfo,
      options: LAUNCH_TIME_MAX_AND_FREE_MEM_OPERATOR,
      convertValue: (value) => {
        if (isPlainObject(value)) return value;
        return {
          operator: FieldsOperator.Lt,
          value: [value],
        };
      },
      compareDisabled: true,
    },
    {
      type: FormType.Select,
      name: FieldKey.ShiplyTag,
      label: hintText(FIELD_LABEL[FieldKey.ShiplyTag], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.ShiplyTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.TestTag,
      fieldScope: Zone.Fd,
      mode: 'tags',
      disabled: !config.enableShiplyAbTestTag,
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.Select,
      name: FieldKey.AbTestTag,
      label: hintText(FIELD_LABEL[FieldKey.AbTestTag], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.AbTestTag],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.AbTestTagLine,
      fieldScope: Zone.Fd,
      mode: 'tags',
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.Select,
      name: FieldKey.TagsInFilter,
      label: FIELD_LABEL[FieldKey.TagsInFilter],
      initialValue: [FIELD_ALL],
      fieldScope: Zone.Fd,
      fieldKey: FieldKey.TagsInFilterUnderline,
      mode: 'tags',
      compareDisabled: true,
      group: FormColumnGroup.Tag,
    },
    // {
    //   type: FormType.Input,
    //   name: FieldKey.AbTestTag,
    //   label: '实验标签',
    //   initialValue: FIELD_ALL,
    // },
    {
      type: FormType.Select,
      name: 'romName',
      label: hintText(FIELD_LABEL[FieldKey.Rom], ABTEST_TIPS),
      labelString: FIELD_LABEL[FieldKey.Rom],
      initialValue: FIELD_ALL,
      fieldKey: FieldKey.Rom,
      fieldScope: Zone.Fd,
      mode: 'tags',
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.SelectNumberGroup,
      name: FieldKey.StandardLaunchDuration,
      label: hintText('使用时长（秒）', numberFilterTips),
      labelString: '使用时长（秒）',
      options: NUMBER_MATCH_OPTIONS,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.TdsArea,
      name: FieldKey.StandardArea,
      label: '地域',
      group: FormColumnGroup.UserInfo,
      labelString: '地域',
      fieldScope: Zone.Fd,
      compareDisabled: true,
      initialDeleted: true,
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.UserCustom,
      label: '自定义维度',
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.CustomKeys,
      name: FieldKey.BizExtendInfo,
      label: '自定义字段',
      group: FormColumnGroup.Custom,
    },
    {
      type: FormType.Select,
      name: 'showNew',
      label: '只显示新增问题',
      disabled: !showSwitch || props.entry !== FilterEntry.MemoryFdList,
      deleteable: false,
      mode: 'single',
      fieldKey: FieldKey.ShowNew,
      fieldScope: Zone.Fd,
      initialValue: false,
      compareDisabled: true,
    },
  ];

  const onFinish = (value: Record<string, any>, compareSwitch?: boolean) => {
    const compareDateValue = getCompareDateValue(value, compareSwitch);
    if (compareSwitch) {
      form.setFieldsValue(compareDateValue);
    }
    props.onFinish({ ...value, ...compareDateValue });
  };

  return <QueryForm
    form={form}
    cachePrefix={'prefix1'}
    ref={tableRef}
    entries={entries}
    onFinish={onFinish}
    onChange={async (__, fields) => onChangeAppVersion({
      values: toArray(fields.appVersionGroup ?? ''),
      operator: fields?.appVersionOperator ?? '',
    }, setShowSwitch)}
    onReset={() => setShowSwitch(false)}
    pageEntry={props.entry}
    issueType={IssueType.ISSUE_TYPE_FD_LEAK}
    hasCompare={props.entry === FilterEntry.MemoryFdList}
    defaultValues={props.initialValue}
    isSaveSearchValues={true}
    searchValues={searchValueDeserializer(searchValues)}
    setSearchValues={setSearchValues}
    renderByGroup
  />;
});
